import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { emitToast } from '../../context/toast';
import ProgressPhotoImage from '../d28d/ProgressPhotoImage';
import { resizeImageFile } from '../../utils/resizeImageFile';

function dateKey(value) {
  const s = String(value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date(s));
  } catch {
    return s.slice(0, 10);
  }
}

function formatDay(key) {
  try {
    return new Intl.DateTimeFormat('es-CO', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'America/Bogota',
    }).format(new Date(`${key}T12:00:00-05:00`));
  } catch {
    return key;
  }
}

export default function TrainingProgressPhotosHistory({
  photos = [],
  measurements = [],
  fields = [],
  rules = null,
  readOnly = false,
  apiBase = '/training/progress-photos',
  mediaApiBase = '/training/progress-photos',
  onChanged,
  onDeleteMeasurement,
}) {
  const [expanded, setExpanded] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const inputRefs = useRef({});
  const positions = (rules?.positions && rules.positions.length)
    ? rules.positions
    : [
      { key: 'frente', label: 'Frente' },
      { key: 'lateral', label: 'Lateral derecha' },
      { key: 'espalda', label: 'Espalda' },
      { key: 'detalle', label: 'Lateral izquierdo' },
    ];

  const days = useMemo(() => {
    const map = new Map();
    for (const m of measurements) {
      const key = dateKey(m.recorded_at);
      if (!key) continue;
      if (!map.has(key)) map.set(key, { key, measurements: [], photos: [] });
      map.get(key).measurements.push(m);
    }
    for (const p of photos) {
      const key = p.date_key || dateKey(p.recorded_at);
      if (!key) continue;
      if (!map.has(key)) map.set(key, { key, measurements: [], photos: [] });
      map.get(key).photos.push(p);
    }
    return [...map.values()].sort((a, b) => b.key.localeCompare(a.key));
  }, [measurements, photos]);

  useEffect(() => {
    if (!expanded && days[0]) setExpanded(days[0].key);
  }, [days, expanded]);

  const uploadForDay = async (dayKey, angle, file) => {
    if (!file || readOnly) return;
    const prepared = await resizeImageFile(file);
    const form = new FormData();
    form.append('file', prepared);
    form.append('angle', angle);
    form.append('recorded_at', dayKey);
    try {
      setUploading(`${dayKey}:${angle}`);
      await api.post(apiBase, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      emitToast({ type: 'success', title: 'Foto guardada' });
      onChanged?.();
    } catch (err) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: err.response?.data?.error || 'No se pudo subir',
      });
    } finally {
      setUploading(null);
    }
  };

  const removePhoto = async (photo) => {
    if (readOnly || !photo?.id) return;
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      setDeletingId(photo.id);
      await api.delete(`${apiBase}/${photo.id}`);
      emitToast({ type: 'success', title: 'Foto eliminada' });
      onChanged?.();
    } catch (err) {
      emitToast({ type: 'error', title: 'Error', message: err.response?.data?.error || 'No se pudo eliminar' });
    } finally {
      setDeletingId(null);
    }
  };

  if (!days.length) {
    return (
      <p className="text-sm text-stone-600 border border-dashed border-stone-300 rounded-xl p-6 text-center bg-white">
        Aún no hay fotos ni medidas en el historial.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {days.map((day) => {
        const open = expanded === day.key;
        return (
          <div key={day.key} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
            <div className="flex items-center justify-between p-3 bg-slate-50">
              <button
                type="button"
                className="text-sm font-semibold text-stone-800 flex-1 text-left"
                onClick={() => setExpanded(open ? null : day.key)}
              >
                {formatDay(day.key)}
              </button>
              <div className="flex items-center gap-2">
                {!readOnly && day.measurements[0] && onDeleteMeasurement ? (
                  <button
                    type="button"
                    className="p-1 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Eliminar medición (las fotos se conservan)"
                    onClick={() => onDeleteMeasurement(day.measurements[0].id)}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
                <button type="button" onClick={() => setExpanded(open ? null : day.key)} aria-label="Expandir">
                  {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
            </div>
            {open ? (
              <div className="p-3 space-y-3">
                {day.measurements.map((h) => (
                  <div key={h.id} className="grid grid-cols-2 gap-2 text-sm">
                    {fields.filter((f) => h[f.key] != null).map((f) => (
                      <div key={f.key} className="flex justify-between bg-slate-50 p-2 rounded-lg">
                        <span className="text-stone-500">{f.label}</span>
                        <span className="font-medium text-stone-900">{h[f.key]}</span>
                      </div>
                    ))}
                    {h.notes ? <p className="col-span-2 text-sm text-stone-600">{h.notes}</p> : null}
                  </div>
                ))}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2 flex items-center gap-1">
                    <Camera size={12} /> Fotos
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {positions.map((pos) => {
                      const existing = day.photos.find((p) => p.angle === pos.key);
                      const busy = uploading === `${day.key}:${pos.key}`;
                      const inputKey = `${day.key}:${pos.key}`;
                      return (
                        <div key={pos.key} className="rounded-lg border border-slate-200 p-2">
                          <p className="text-[11px] font-bold uppercase text-stone-600 mb-1">{pos.label}</p>
                          {existing ? (
                            <button
                              type="button"
                              className="w-full"
                              onClick={() => setLightbox({ src: existing.url, alt: pos.label })}
                            >
                              <ProgressPhotoImage
                                src={existing.url}
                                alt={pos.label}
                                className="w-full h-28 object-cover rounded-md"
                                linkable={false}
                                mediaApiBase={mediaApiBase}
                              />
                            </button>
                          ) : (
                            <div className="h-28 rounded-md bg-slate-100 flex items-center justify-center text-xs text-stone-500">
                              {readOnly ? 'Pendiente' : (busy ? 'Subiendo…' : 'Sin foto')}
                            </div>
                          )}
                          {!readOnly ? (
                            <>
                              <input
                                ref={(el) => { inputRefs.current[inputKey] = el; }}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  uploadForDay(day.key, pos.key, e.target.files?.[0]);
                                  e.target.value = '';
                                }}
                              />
                              <div className="flex gap-1 mt-1">
                                <button
                                  type="button"
                                  className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-300 text-stone-700 flex-1"
                                  disabled={busy}
                                  onClick={() => inputRefs.current[inputKey]?.click()}
                                >
                                  {existing ? 'Cambiar' : 'Subir'}
                                </button>
                                {existing ? (
                                  <button
                                    type="button"
                                    className="text-[11px] font-semibold px-2 py-1 rounded border border-red-200 text-red-700"
                                    disabled={deletingId === existing.id}
                                    onClick={() => removePhoto(existing)}
                                  >
                                    Borrar
                                  </button>
                                ) : null}
                              </div>
                            </>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
      {lightbox ? (
        <button
          type="button"
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <ProgressPhotoImage
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[80vh] w-full object-contain rounded-lg"
              linkable={false}
              mediaApiBase={mediaApiBase}
            />
            <button type="button" className="btn-secondary mt-3" onClick={() => setLightbox(null)}>Cerrar</button>
          </div>
        </button>
      ) : null}
    </div>
  );
}

export { dateKey };
